<template>
  <div class="dashboard-resident-vital-signs-list">
    <NavBar />
    <AuthBySessionExpired />

    <div class="container pt-0 mb-5">
      <!-- Header Section -->
      <div class="dashboard-resident-vital-signs-list-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn class="btn-return" @click="handleToBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>

          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-heartbeat title-icon"></i>
              Sinais Vitais dos Residentes
            </h1>
            <p class="page-subtitle">Monitoramento e histórico de medições</p>
          </div>

          <div class="vs-status-legend">
            <span class="vs-status-legend__title">Legenda:</span>
            <span class="vs-status-legend__item vs-status-legend__item--normal">
              <span class="vs-status-legend__dot"></span> Normal
            </span>
            <span
              class="vs-status-legend__item vs-status-legend__item--warning"
            >
              <span class="vs-status-legend__dot"></span> Atenção
            </span>
            <span class="vs-status-legend__item vs-status-legend__item--danger">
              <span class="vs-status-legend__dot"></span> Crítico
            </span>
          </div>
        </div>
      </div>

      <!-- ============================================
           PAINEL DE FILTROS COLAPSÁVEL
           ============================================ -->
      <section class="vs-filters-panel">
        <button
          class="vs-filters-panel__toggle"
          @click="showFilters = !showFilters"
          :aria-expanded="showFilters"
          aria-controls="filters-content"
        >
          <i class="fas fa-sliders-h vs-filters-panel__toggle-icon"></i>
          <span>Filtros</span>
          <span class="vs-filters-panel__badge" v-if="activeFiltersCount > 0">
            {{ activeFiltersCount }}
          </span>
          <i
            class="fas vs-filters-panel__chevron"
            :class="showFilters ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </button>

        <transition name="vs-slide">
          <div
            v-show="showFilters"
            id="filters-content"
            class="vs-filters-panel__content"
          >
            <div class="vs-filters-grid">
              <!-- Filtro: Pesquisa por Nome -->
              <div class="vs-filter-item vs-filter-item--search">
                <label class="vs-filter-item__label" for="filter-name">
                  <i class="fas fa-search"></i> Pesquisar
                </label>
                <v-text-field
                  id="filter-name"
                  dense
                  placeholder="Digite o nome do residente"
                  label="Nome do residente"
                  outlined
                  v-model="name"
                  @input="getResidents"
                  hide-details
                  clearable
                />
              </div>

              <!-- Filtro: Gênero -->
              <div class="vs-filter-item">
                <label class="vs-filter-item__label" for="filter-gender">
                  <i class="fas fa-venus-mars"></i> Gênero
                </label>
                <v-select
                  id="filter-gender"
                  dense
                  :items="genders"
                  v-model="gender"
                  label="Gênero"
                  outlined
                  @change="getResidents"
                  hide-details
                />
              </div>

              <!-- Filtro: Grau de Dependência -->
              <div class="vs-filter-item">
                <label class="vs-filter-item__label" for="filter-degree">
                  <i class="fas fa-user-shield"></i> Dependência
                </label>
                <v-select
                  id="filter-degree"
                  dense
                  :items="typesFilterByGrau"
                  v-model="degree_of_dependence_id"
                  label="Grau de dependência"
                  outlined
                  @change="getResidents"
                  hide-details
                />
              </div>

              <!-- Filtro: Período -->
              <div class="vs-filter-item">
                <label class="vs-filter-item__label" for="filter-period">
                  <i class="fas fa-calendar-alt"></i> Período
                </label>
                <v-select
                  id="filter-period"
                  @input="getResidents"
                  label="Período"
                  v-model="next_or_previous"
                  :items="[
                    { value: 'PREV', text: 'Datas anteriores' },
                    { value: 'NEXT', text: 'Próximas datas' },
                  ]"
                  dense
                  outlined
                  item-value="value"
                  item-text="text"
                  hide-details
                />
              </div>

              <!-- Filtro: Data -->
              <div class="vs-filter-item">
                <label class="vs-filter-item__label" for="filter-date">
                  <i class="fas fa-calendar-day"></i> Data
                </label>
                <v-text-field
                  id="filter-date"
                  label="Data de referência"
                  outlined
                  dense
                  type="date"
                  v-model="date"
                  @input="getResidents"
                  hide-details
                />
              </div>
            </div>

            <!-- Botão Limpar Filtros -->
            <div
              class="vs-filters-panel__actions"
              v-if="activeFiltersCount > 0"
            >
              <button class="vs-btn-clear-filters" @click="clearFilters">
                <i class="fas fa-times-circle"></i> Limpar filtros
              </button>
            </div>
          </div>
        </transition>
      </section>

      <!-- ============================================
           ÁREA DE CONTEÚDO PRINCIPAL
           ============================================ -->
      <main class="vs-main-content">
        <b-overlay :show="loading" opacity="0.85" rounded="lg">
          <template #overlay>
            <div class="vs-loading-state">
              <div class="vs-loading-state__spinner"></div>
              <p class="vs-loading-state__text">Carregando dados...</p>
            </div>
          </template>

          <!-- Lista de Residentes -->
          <div class="vs-residents-list" v-if="residents.length > 0">
            <article
              v-for="resident in residents"
              :key="resident.id"
              class="vs-resident-card"
            >
              <!-- Cabeçalho do Card do Residente -->
              <header class="vs-resident-card__header">
                <div class="vs-resident-card__avatar-container">
                  <img
                    class="vs-resident-card__avatar"
                    :src="getResidentPhoto(resident)"
                    :alt="`Foto de ${resident.name}`"
                  />
                </div>

                <div class="vs-resident-card__info">
                  <h2 class="vs-resident-card__name">
                    {{ resident.name }}
                  </h2>
                  <div class="vs-resident-card__meta">
                    <span
                      class="vs-resident-card__age"
                      v-if="resident.date_of_birth"
                    >
                      <i class="fas fa-birthday-cake"></i>
                      {{ getAge(resident.date_of_birth) }} anos
                    </span>
                  </div>
                </div>

                <button
                  class="vs-resident-card__action-btn"
                  @click="handleToResident(resident.id)"
                  :aria-label="`Acessar prontuário de ${resident.name}`"
                >
                  <span>Acessar Residente</span>
                  <i class="fas fa-arrow-right"></i>
                </button>
              </header>

              <!-- Corpo do Card: Tabela de Sinais Vitais -->
              <div class="vs-resident-card__body">
                <!-- Verificar se há dados -->
                <div
                  v-if="!hasVitalSignsData(resident)"
                  class="vs-empty-vital-signs"
                >
                  <i class="fas fa-clipboard-list"></i>
                  <p>Nenhum sinal vital registrado no período selecionado</p>
                </div>

                <!-- Tabela de Sinais Vitais -->
                <div v-else class="vs-vital-signs-table-wrapper">
                  <table class="vs-vital-signs-table">
                    <thead>
                      <tr>
                        <th class="vs-vital-signs-table__header-type">
                          Medição
                        </th>
                        <th
                          v-for="item in resident.vital_signs"
                          :key="item.date"
                          class="vs-vital-signs-table__header-date"
                        >
                          <span class="vs-date-header">
                            <span class="vs-date-header__day">
                              {{ formatDateDay(item.date) }}
                            </span>
                            <span class="vs-date-header__month">
                              {{ formatDateMonth(item.date) }}
                            </span>
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="type in typesVitalSigns"
                        :key="type"
                        class="vs-vital-signs-table__row"
                      >
                        <!-- Coluna: Tipo de Medição -->
                        <td class="vs-vital-signs-table__cell-type">
                          <span class="vs-vital-type">
                            <i :class="getVitalSignIcon(type)"></i>
                            {{ formatVitalType(type) }}
                          </span>
                        </td>

                        <!-- Colunas: Valores por Data -->
                        <td
                          v-for="(vital_sign, key) in resident.vital_signs"
                          :key="key"
                          class="vs-vital-signs-table__cell-value"
                        >
                          <!-- Sem dados -->
                          <span
                            v-if="!hasDataForType(vital_sign, type)"
                            class="vs-vital-value vs-vital-value--empty"
                          >
                            —
                          </span>

                          <!-- ALIMENTAÇÃO -->
                          <template v-if="type === TYPE_ALIMENTACAO">
                            <div
                              v-for="item in vital_sign.care_has_feeding_care"
                              :key="item.id"
                              class="vs-vital-value-container"
                            >
                              <button
                                :class="[
                                  'vs-vital-btn',
                                  getFeedingStatusClass(item.response),
                                ]"
                                v-tippy="{
                                  content: buildTooltip(item, 'feeding'),
                                  followCursor: true,
                                }"
                              >
                                <i :class="getFeedingIcon(item.response)"></i>
                                <span class="vs-vital-btn__text">
                                  {{ item.name }} -
                                  {{ formatFeedingResponse(item) }}
                                </span>
                              </button>
                            </div>
                          </template>

                          <!-- OUTROS SINAIS VITAIS -->
                          <template v-if="type !== TYPE_ALIMENTACAO">
                            <div
                              v-for="(
                                care_vital_sign, index
                              ) in vital_sign.care_vital_signs"
                              :key="index"
                            >
                              <template
                                v-for="item in care_vital_sign.care_has_vital_signs"
                              >
                                <div
                                  v-if="item.type === type"
                                  :key="item.id"
                                  class="vs-vital-value-container"
                                >
                                  <button
                                    :class="[
                                      'vs-vital-btn',
                                      getVitalStatusClass(type, item),
                                    ]"
                                    v-tippy="{
                                      content: buildTooltip(item, type),
                                      followCursor: true,
                                    }"
                                  >
                                    <span class="vs-vital-btn__value">
                                      {{ formatVitalValue(type, item) }}
                                    </span>
                                    <span
                                      v-if="item.abbreviation"
                                      class="vs-vital-btn__unit"
                                    >
                                      {{ item.abbreviation }}
                                    </span>
                                  </button>
                                </div>
                              </template>
                            </div>
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </article>
          </div>

          <!-- Estado Vazio -->
          <div v-else class="vs-empty-state">
            <div class="vs-empty-state__icon">
              <i class="fas fa-users-slash"></i>
            </div>
            <h3 class="vs-empty-state__title">Nenhum residente encontrado</h3>
            <p class="vs-empty-state__description">
              Tente ajustar os filtros ou verificar os critérios de busca.
            </p>
            <button
              v-if="activeFiltersCount > 0"
              class="vs-empty-state__action"
              @click="clearFilters"
            >
              <i class="fas fa-redo"></i> Limpar filtros
            </button>
          </div>
        </b-overlay>

        <!-- Mensagens de Erro -->
        <ErrorsMessages :errors="errors" />
      </main>

      <!-- ============================================
           PAGINAÇÃO
           ============================================ -->
      <nav
        v-if="residents.length > 0"
        class="vs-pagination-container"
        aria-label="Navegação de páginas"
      >
        <b-pagination
          align="center"
          @input="getResidents()"
          v-model="page"
          :total-rows="total"
          :per-page="itemsPerPage"
          first-number
          last-number
        />
        <p class="vs-pagination-info">
          Exibindo {{ residents.length }} de {{ total }} residentes
        </p>
      </nav>
    </div>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import api from "../services/api";
import { getAge } from "../lib/getAgeByDate";
import routes from "../routes";
import permission from "../lib/permission";
import { userData } from "../lib/functions";
import { formatDate } from "../lib/format";
import moment from "moment";

export default {
  name: "ResidentsVitalSignsList",

  components: {
    NavBar,
    AuthBySessionExpired,
    ErrorsMessages,
  },

  data() {
    return {
      // Controle de UI
      showFilters: true,

      // Paginação
      page: 1,
      total: 0,
      itemsPerPage: 10,

      // Filtros
      next_or_previous: "PREV",
      date: moment().format("YYYY-MM-DD"),
      name: "",
      gender: "NONE",
      filterCare: "NONE",
      degree_of_dependence_id: "NONE",

      // Tipos de Sinais Vitais
      typesVitalSigns: [
        "ALIMENTAÇÃO",
        "DIURESE",
        "DOR",
        "EVACUAÇÃO",
        "FREQUÊNCIA RESPIRATÓRIA",
        "GLICEMIA CAPILAR",
        "PRESSÃO ARTERIAL",
        "PULSO (FREQUÊNCIA CARDÍACA)",
        "SATURAÇÃO DE OXIGÊNIO",
        "TEMPERATURA CORPORAL",
        // "PESO",
        // "ALTURA",
        "IMC",
      ],

      // Constantes de Tipo
      TYPE_ALIMENTACAO: "ALIMENTAÇÃO",
      TYPE_DIURESE: "DIURESE",
      TYPE_DOR: "DOR",
      TYPE_EVACUACAO: "EVACUAÇÃO",
      TYPE_FREQUENCIA_RESPIRATORIA: "FREQUÊNCIA RESPIRATÓRIA",
      TYPE_GLICEMIA_CAPILAR: "GLICEMIA CAPILAR",
      TYPE_PRESSAO_ARTERIAL: "PRESSÃO ARTERIAL",
      TYPE_PULSO_FREQUENCIA_CARDIACA: "PULSO (FREQUÊNCIA CARDÍACA)",
      TYPE_SATURACAO_DE_OXIGENIO: "SATURAÇÃO DE OXIGÊNIO",
      TYPE_TEMPERATURA_CORPORAL: "TEMPERATURA CORPORAL",
      TYPE_PESO: "PESO",
      TYPE_ALTURA: "ALTURA",
      TYPE_IMC: "IMC",

      // Opções de Filtro
      typesFilterByGrau: [
        { value: "NONE", text: "Todos" },
        { value: "GRAU1", text: "Grau 1" },
        { value: "GRAU2", text: "Grau 2" },
        { value: "GRAU3", text: "Grau 3" },
      ],

      genders: [
        { value: "NONE", text: "Todos" },
        { value: "FEMININO", text: "Feminino" },
        { value: "MASCULINO", text: "Masculino" },
      ],

      // Dados
      filter: [],
      residents: [],
      user: {},

      // Estado
      loading: false,
      errors: [],
    };
  },

  computed: {
    /**
     * Conta quantos filtros estão ativos
     * Ajuda o usuário a entender que há filtros aplicados
     */
    activeFiltersCount() {
      let count = 0;
      if (this.name) count++;
      if (this.gender !== "NONE") count++;
      if (this.degree_of_dependence_id !== "NONE") count++;
      return count;
    },
  },

  created() {
    this.retrieve();
  },

  methods: {
    userData,
    permission,
    getAge,
    formatDate,

    handleToBack: () => routes.go(-1),

    // ============================================
    // MÉTODOS DE FORMATAÇÃO
    // ============================================

    /**
     * Formata o dia da data para exibição no cabeçalho
     */
    formatDateDay(dateStr) {
      return moment(dateStr).format("DD");
    },

    /**
     * Formata o mês da data para exibição no cabeçalho
     */
    formatDateMonth(dateStr) {
      return moment(dateStr).format("MMM").toUpperCase();
    },

    /**
     * Formata o tipo de sinal vital para exibição mais curta
     */
    formatVitalType(type) {
      const shortNames = {
        ALIMENTAÇÃO: "Alimentação",
        DIURESE: "Diurese",
        DOR: "Dor",
        EVACUAÇÃO: "Evacuação",
        "FREQUÊNCIA RESPIRATÓRIA": "Freq. Resp.",
        "GLICEMIA CAPILAR": "Glicemia",
        "PRESSÃO ARTERIAL": "Pressão Art.",
        "PULSO (FREQUÊNCIA CARDÍACA)": "FC/Pulso",
        "SATURAÇÃO DE OXIGÊNIO": "SpO₂",
        "TEMPERATURA CORPORAL": "Temperatura",
        PESO: "Peso",
        ALTURA: "Altura",
        IMC: "IMC",
      };
      return shortNames[type] || type;
    },

    /**
     * Formata a resposta de alimentação de forma mais curta
     */
    formatFeedingResponse(item) {
      const shortResponses = {
        "BOA ACEITAÇÃO": "Boa",
        "ACEITOU PARCIAL": "Parcial",
        "NÃO ACEITOU": "Recusou",
      };
      return shortResponses[item.response] || item.response;
    },

    /**
     * Formata o valor do sinal vital baseado no tipo
     */
    formatVitalValue(type, item) {
      if (type === this.TYPE_DOR) {
        return this.handleDescriptionAche(item.description);
      }
      return item.description;
    },

    // ============================================
    // MÉTODOS DE ÍCONES
    // ============================================

    /**
     * Retorna o ícone apropriado para cada tipo de sinal vital
     */
    getVitalSignIcon(type) {
      const icons = {
        ALIMENTAÇÃO: "fas fa-utensils",
        DIURESE: "fas fa-tint",
        DOR: "fas fa-head-side-virus",
        EVACUAÇÃO: "fas fa-toilet",
        "FREQUÊNCIA RESPIRATÓRIA": "fas fa-lungs",
        "GLICEMIA CAPILAR": "fas fa-tint",
        "PRESSÃO ARTERIAL": "fas fa-heart",
        "PULSO (FREQUÊNCIA CARDÍACA)": "fas fa-heartbeat",
        "SATURAÇÃO DE OXIGÊNIO": "fas fa-percentage",
        "TEMPERATURA CORPORAL": "fas fa-thermometer-half",
        PESO: "fas fa-weight",
        ALTURA: "fas fa-ruler-vertical",
        IMC: "fas fa-pencil-ruler",
      };
      return icons[type] || "fas fa-notes-medical";
    },

    /**
     * Retorna ícone baseado na resposta de alimentação
     */
    getFeedingIcon(response) {
      const icons = {
        "BOA ACEITAÇÃO": "fas fa-check-circle",
        "ACEITOU PARCIAL": "fas fa-minus-circle",
        "NÃO ACEITOU": "fas fa-times-circle",
      };
      return icons[response] || "fas fa-question-circle";
    },

    // ============================================
    // MÉTODOS DE STATUS/CLASSE CSS
    // ============================================

    /**
     * Retorna a classe CSS para status de alimentação
     */
    getFeedingStatusClass(response) {
      const classes = {
        "BOA ACEITAÇÃO": "vs-vital-btn--success",
        "ACEITOU PARCIAL": "vs-vital-btn--warning",
        "NÃO ACEITOU": "vs-vital-btn--danger",
      };
      return classes[response] || "";
    },

    /**
     * Retorna a classe CSS baseada no tipo e valor do sinal vital
     */
    getVitalStatusClass(type, item) {
      const value = parseFloat(item.description ? item.description : 0);

      switch (type) {
        case this.TYPE_DIURESE:
          return this.getDiureseClass(item.description);

        case this.TYPE_DOR:
          return this.getPainClass(value);

        case this.TYPE_EVACUACAO:
          return this.getEvacuationClass(item.description);

        case this.TYPE_FREQUENCIA_RESPIRATORIA:
          return this.getRespiratoryClass(value);

        case this.TYPE_GLICEMIA_CAPILAR:
          return this.getGlycemiaClass(value);

        case this.TYPE_PRESSAO_ARTERIAL:
          return this.handleColorBloodPressure(
            item.initial_description,
            item.final_description
          );

        case this.TYPE_PULSO_FREQUENCIA_CARDIACA:
          return this.getHeartRateClass(value);

        case this.TYPE_SATURACAO_DE_OXIGENIO:
          return this.getOxygenClass(value);

        case this.TYPE_TEMPERATURA_CORPORAL:
          return this.getTemperatureClass(value);

        case this.TYPE_PESO:
          return "vs-vital-btn--neutral";

        case this.TYPE_ALTURA:
          return "vs-vital-btn--neutral";

        default:
          return "";
      }
    },

    getDiureseClass(description) {
      const classes = {
        "POUCA DIURESE": "vs-vital-btn--warning",
        "DIURESE PRESENTE": "vs-vital-btn--success",
        "AUSÊNCIA DE DIURESE": "vs-vital-btn--danger",
      };
      return classes[description] || "";
    },

    getPainClass(value) {
      if (value >= 0 && value <= 3) return "vs-vital-btn--success";
      if (value >= 4 && value <= 7) return "vs-vital-btn--warning";
      if (value >= 8 && value <= 10) return "vs-vital-btn--danger";
      return "";
    },

    getEvacuationClass(description) {
      const classes = {
        PASTOSA: "vs-vital-btn--warning",
        "EM CÍBALOS": "vs-vital-btn--warning",
        "BEM MOLDADAS": "vs-vital-btn--success",
        AUSENTE: "vs-vital-btn--danger",
        LÍQUIDA: "vs-vital-btn--danger",
      };
      return classes[description] || "";
    },

    getRespiratoryClass(value) {
      if (value >= 10 && value <= 20) return "vs-vital-btn--success";
      if (value >= 21 && value <= 34) return "vs-vital-btn--warning";
      return "vs-vital-btn--danger";
    },

    getGlycemiaClass(value) {
      if (value >= 70 && value <= 110) return "vs-vital-btn--success";
      if ((value >= 51 && value <= 69) || (value >= 111 && value <= 125)) {
        return "vs-vital-btn--warning";
      }
      return "vs-vital-btn--danger";
    },

    getHeartRateClass(value) {
      if (value >= 60 && value <= 100) return "vs-vital-btn--success";
      if ((value >= 41 && value <= 59) || (value >= 101 && value <= 139)) {
        return "vs-vital-btn--warning";
      }
      return "vs-vital-btn--danger";
    },

    getOxygenClass(value) {
      if (value >= 92 && value <= 100) return "vs-vital-btn--success";
      if (value >= 88 && value <= 91) return "vs-vital-btn--warning";
      return "vs-vital-btn--danger";
    },

    getTemperatureClass(value) {
      if (value >= 36.1 && value <= 37.2) return "vs-vital-btn--success";
      if ((value >= 35.1 && value <= 36) || (value >= 37.3 && value <= 38.4)) {
        return "vs-vital-btn--warning";
      }
      return "vs-vital-btn--danger";
    },

    handleColorBloodPressure(initial, final) {
      const sys = parseInt(initial);
      const dia = parseInt(final);

      if (sys >= 100 && sys <= 140) {
        if (dia >= 60 && dia <= 90) return "vs-vital-btn--success";
        if (dia >= 50 && dia <= 59) return "vs-vital-btn--warning";
      }

      if (sys >= 141 && sys <= 179) {
        if (dia >= 91 && dia <= 109) return "vs-vital-btn--warning";
        return "vs-vital-btn--danger";
      }

      return "vs-vital-btn--danger";
    },

    handleDescriptionAche(nivel) {
      const n = parseInt(nivel);
      if (n === 0 || !nivel) return "Sem dor";
      if (n >= 1 && n <= 3) return "Leve";
      if (n >= 4 && n <= 7) return "Moderada";
      if (n >= 8 && n <= 10) return "Intensa";
      return nivel;
    },

    // ============================================
    // MÉTODOS AUXILIARES
    // ============================================

    /**
     * Retorna a URL da foto do residente
     */
    getResidentPhoto(resident) {
      if (this.permission("read", "resident_photo") && resident.photo_url) {
        return resident.photo_url;
      }
      return require("../assets/user.png");
    },

    /**
     * Verifica se o residente possui dados de sinais vitais
     */
    hasVitalSignsData(resident) {
      if (!resident.vital_signs || resident.vital_signs.length === 0) {
        return false;
      }

      return resident.vital_signs.some(
        (vs) =>
          (vs.care_vital_signs && vs.care_vital_signs.length > 0) ||
          (vs.care_has_feeding_care && vs.care_has_feeding_care.length > 0)
      );
    },

    /**
     * Verifica se há dados para um tipo específico em uma data
     */
    hasDataForType(vital_sign, type) {
      if (type === this.TYPE_ALIMENTACAO) {
        return (
          vital_sign.care_has_feeding_care &&
          vital_sign.care_has_feeding_care.length > 0
        );
      }

      if (!vital_sign.care_vital_signs) return false;

      return vital_sign.care_vital_signs.some(
        (cvs) =>
          cvs.care_has_vital_signs &&
          cvs.care_has_vital_signs.some((item) => item.type === type)
      );
    },

    /**
     * Constrói o conteúdo do tooltip
     */
    buildTooltip(item, type) {
      const date = this.formatDate(item.created_at);
      const user = item.user_name || "Não informado";

      let value = "";
      if (type === "feeding") {
        value = `${item.name} - ${item.response}`;
      } else if (type === this.TYPE_DOR) {
        value = this.handleDescriptionAche(item.description);
      } else {
        value = `${item.description} ${item.abbreviation || ""}`;
      }

      return `
        <div class="vs-tooltip-content">
          <p><strong>Data:</strong> ${date}</p>
          <p><strong>Valor:</strong> ${value}</p>
          <p><strong>Registrado por:</strong> ${user}</p>
        </div>
      `;
    },

    /**
     * Limpa todos os filtros e recarrega os dados
     */
    clearFilters() {
      this.name = "";
      this.gender = "NONE";
      this.degree_of_dependence_id = "NONE";
      this.page = 1;
      this.getResidents();
    },

    // ============================================
    // MÉTODOS DE DADOS
    // ============================================

    async retrieve() {
      const { user } = this.userData();
      this.user = user;
      this.getResidents();
    },

    async getResidents() {
      try {
        this.loading = true;
        this.residents = [];

        const params = {
          page: this.page,
          itemsPerPage: this.itemsPerPage,
          name: this.name,
          degree_of_dependence_id: this.degree_of_dependence_id,
          gender: this.gender,
          next_or_previous: this.next_or_previous,
          date: this.date,
        };

        const response = await api.get(
          "/rest-home/resident/dashboard/vital-signs-list",
          { params }
        );

        const { residents, error, errors, total } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.filter = residents;
          this.residents = residents;
          this.total = total;
          this.errors = [];
        }
      } catch (error) {
        console.error("Erro ao carregar residentes:", error);
        this.errors = ["Erro ao carregar dados. Tente novamente."];
      } finally {
        this.loading = false;
      }
    },

    handleToResident(id) {
      let route = routes.resolve({
        name: "resident",
        params: {
          id,
        },
        query: {
          tab: "check",
          nav: "check",
        },
      });

      window.open(route.href, "_blank");
    },
  },
};
</script>

<style scoped>
/* ============================================
   VARIÁVEIS CSS LOCAIS
   ============================================ */
.dashboard-resident-vital-signs-list {
  --vs-primary: #33495e;
  --vs-primary-light: #4a6785;
  --vs-success: #28a745;
  --vs-success-light: #d4edda;
  --vs-warning: #ffc107;
  --vs-warning-light: #fff3cd;
  --vs-danger: #dc3545;
  --vs-danger-light: #f8d7da;
  --vs-neutral: #6c757d;
  --vs-neutral-light: #e9ecef;
  --vs-bg: #f8f9fa;
  --vs-border: #dee2e6;
  --vs-text: #212529;
  --vs-text-muted: #6c757d;
  --vs-radius: 8px;
  --vs-radius-sm: 4px;
  --vs-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  --vs-shadow-lg: 0 4px 16px rgba(0, 0, 0, 0.12);
  --vs-transition: 0.2s ease;
}

/* Header Section */
.dashboard-resident-vital-signs-list-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

/* Legenda de Status */
.vs-status-legend {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.15);
  border-radius: var(--vs-radius-sm);
  font-size: 0.85rem;
}

.vs-status-legend__title {
  font-weight: 600;
}

.vs-status-legend__item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.vs-status-legend__dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
}

.vs-status-legend__item--normal .vs-status-legend__dot {
  background: var(--vs-success);
}

.vs-status-legend__item--warning .vs-status-legend__dot {
  background: var(--vs-warning);
}

.vs-status-legend__item--danger .vs-status-legend__dot {
  background: var(--vs-danger);
}

/* ============================================
   PAINEL DE FILTROS
   ============================================ */
.vs-filters-panel {
  margin-bottom: 1.5rem;
  background: #fff;
  border-radius: var(--vs-radius);
  box-shadow: var(--vs-shadow);
  overflow: hidden;
}

.vs-filters-panel__toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 1rem 1.25rem;
  background: #fff;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  color: var(--vs-primary);
  transition: background var(--vs-transition);
}

.vs-filters-panel__toggle:hover {
  background: var(--vs-bg);
}

.vs-filters-panel__toggle-icon {
  font-size: 1.1rem;
}

.vs-filters-panel__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: var(--vs-primary);
  color: #fff;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
}

.vs-filters-panel__chevron {
  margin-left: auto;
  font-size: 0.9rem;
  transition: transform var(--vs-transition);
}

.vs-filters-panel__content {
  padding: 0 1.25rem 1.25rem;
  border-top: 1px solid var(--vs-border);
}

/* Grid de Filtros */
.vs-filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  padding-top: 1rem;
}

.vs-filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.vs-filter-item--search {
  grid-column: span 2;
}

.vs-filter-item__label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--vs-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.vs-filter-item__label i {
  font-size: 0.9rem;
  color: var(--vs-primary);
}

.vs-filters-panel__actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid var(--vs-border);
  margin-top: 1rem;
}

.vs-btn-clear-filters {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid var(--vs-danger);
  border-radius: var(--vs-radius-sm);
  color: var(--vs-danger);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--vs-transition);
}

.vs-btn-clear-filters:hover {
  background: var(--vs-danger);
  color: #fff;
}

/* Animação de slide */
.vs-slide-enter-active,
.vs-slide-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.vs-slide-enter,
.vs-slide-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

/* ============================================
   CONTEÚDO PRINCIPAL
   ============================================ */
.vs-main-content {
  min-height: 400px;
}

/* Estado de Loading */
.vs-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
}

.vs-loading-state__spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--vs-border);
  border-top-color: var(--vs-primary);
  border-radius: 50%;
  animation: vs-spin 1s linear infinite;
}

@keyframes vs-spin {
  to {
    transform: rotate(360deg);
  }
}

.vs-loading-state__text {
  margin-top: 1rem;
  color: var(--vs-text-muted);
  font-size: 0.95rem;
}

/* ============================================
   CARDS DE RESIDENTES
   ============================================ */
.vs-residents-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.vs-resident-card {
  background: #fff;
  border-radius: var(--vs-radius);
  box-shadow: var(--vs-shadow);
  overflow: hidden;
  transition: box-shadow var(--vs-transition);
}

.vs-resident-card:hover {
  box-shadow: var(--vs-shadow-lg);
}

/* Cabeçalho do Card */
.vs-resident-card__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(to right, var(--vs-bg), #fff);
  border-bottom: 1px solid var(--vs-border);
}

.vs-resident-card__avatar-container {
  flex-shrink: 0;
}

.vs-resident-card__avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.vs-resident-card__info {
  flex: 1;
  min-width: 0;
}

.vs-resident-card__name {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--vs-primary);
  margin: 0 0 0.25rem;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vs-resident-card__meta {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.9rem;
  color: var(--vs-text-muted);
}

.vs-resident-card__age {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.vs-resident-card__age i {
  color: var(--vs-primary);
}

.vs-resident-card__action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  background: var(--vs-primary);
  color: #fff;
  border: none;
  border-radius: var(--vs-radius-sm);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--vs-transition);
  white-space: nowrap;
}

.vs-resident-card__action-btn:hover {
  background: var(--vs-primary-light);
  transform: translateX(4px);
}

.vs-resident-card__action-btn i {
  font-size: 0.85rem;
  transition: transform var(--vs-transition);
}

.vs-resident-card__action-btn:hover i {
  transform: translateX(4px);
}

/* Corpo do Card */
.vs-resident-card__body {
  padding: 1rem;
}

/* Estado vazio de sinais vitais */
.vs-empty-vital-signs {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: var(--vs-text-muted);
  text-align: center;
}

.vs-empty-vital-signs i {
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  opacity: 0.5;
}

.vs-empty-vital-signs p {
  margin: 0;
  font-size: 0.95rem;
}

/* ============================================
   TABELA DE SINAIS VITAIS
   ============================================ */
.vs-vital-signs-table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.vs-vital-signs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.vs-vital-signs-table th,
.vs-vital-signs-table td {
  padding: 0.6rem 0.5rem;
  text-align: center;
  border: 1px solid var(--vs-border);
}

/* Cabeçalho da tabela */
.vs-vital-signs-table thead {
  background: var(--vs-bg);
}

.vs-vital-signs-table__header-type {
  position: sticky;
  left: 0;
  background: var(--vs-primary);
  color: #fff;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  min-width: 120px;
  z-index: 2;
}

.vs-vital-signs-table__header-date {
  min-width: 90px;
  background: var(--vs-bg);
}

.vs-date-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
}

.vs-date-header__day {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--vs-primary);
}

.vs-date-header__month {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--vs-text-muted);
  text-transform: uppercase;
}

/* Células da tabela */
.vs-vital-signs-table__row:nth-child(even) {
  background: rgba(0, 0, 0, 0.02);
}

.vs-vital-signs-table__cell-type {
  position: sticky;
  left: 0;
  background: #fff;
  font-weight: 500;
  text-align: left;
  padding-left: 0.75rem;
  z-index: 1;
}

.vs-vital-signs-table__row:nth-child(even) .vs-vital-signs-table__cell-type {
  background: #fafafa;
}

.vs-vital-type {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--vs-text);
}

.vs-vital-type i {
  font-size: 0.9rem;
  color: var(--vs-primary);
  width: 20px;
  text-align: center;
}

.vs-vital-signs-table__cell-value {
  min-width: 90px;
  vertical-align: middle;
}

/* Valores dos sinais vitais */
.vs-vital-value-container {
  margin: 0.25rem 0;
}

.vs-vital-value--empty {
  color: var(--vs-text-muted);
  font-size: 1.2rem;
}

/* Botões de valores */
.vs-vital-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.35rem 0.6rem;
  border: none;
  border-radius: var(--vs-radius-sm);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--vs-transition);
  min-width: 70px;
}

.vs-vital-btn:hover {
  transform: scale(1.05);
}

.vs-vital-btn--success {
  background: var(--vs-success);
  color: #fff;
}

.vs-vital-btn--warning {
  background: var(--vs-warning);
  color: #212529;
}

.vs-vital-btn--danger {
  background: var(--vs-danger);
  color: #fff;
}

.vs-vital-btn--neutral {
  background: var(--vs-neutral-light);
  color: var(--vs-text);
}

.vs-vital-btn__text {
  white-space: nowrap;
}

.vs-vital-btn__value {
  font-weight: 600;
}

.vs-vital-btn__unit {
  font-size: 0.7rem;
  opacity: 0.9;
}

/* ============================================
   ESTADO VAZIO
   ============================================ */
.vs-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: #fff;
  border-radius: var(--vs-radius);
  box-shadow: var(--vs-shadow);
}

.vs-empty-state__icon {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--vs-neutral-light);
  border-radius: 50%;
  margin-bottom: 1.5rem;
}

.vs-empty-state__icon i {
  font-size: 2.5rem;
  color: var(--vs-text-muted);
}

.vs-empty-state__title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--vs-text);
  margin: 0 0 0.5rem;
}

.vs-empty-state__description {
  font-size: 0.95rem;
  color: var(--vs-text-muted);
  margin: 0 0 1.5rem;
  max-width: 320px;
}

.vs-empty-state__action {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  background: var(--vs-primary);
  color: #fff;
  border: none;
  border-radius: var(--vs-radius-sm);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background var(--vs-transition);
}

.vs-empty-state__action:hover {
  background: var(--vs-primary-light);
}

/* ============================================
   PAGINAÇÃO
   ============================================ */
.vs-pagination-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-top: 2rem;
  padding: 1rem;
  background: #fff;
  border-radius: var(--vs-radius);
  box-shadow: var(--vs-shadow);
}

.vs-pagination-info {
  font-size: 0.85rem;
  color: var(--vs-text-muted);
  margin: 0;
}

/* ============================================
   TOOLTIP CUSTOMIZADO
   ============================================ */
.vs-tooltip-content {
  text-align: left;
  font-size: 0.85rem;
  line-height: 1.5;
}

.vs-tooltip-content p {
  margin: 0.25rem 0;
}

.vs-tooltip-content strong {
  color: var(--vs-primary);
}

/* ============================================
   RESPONSIVIDADE
   ============================================ */
@media (max-width: 768px) {
  .vs-status-legend {
    width: 100%;
    justify-content: center;
  }

  .vs-filter-item--search {
    grid-column: span 1;
  }

  .vs-resident-card__header {
    flex-wrap: wrap;
  }

  .vs-resident-card__action-btn {
    width: 100%;
    justify-content: center;
    margin-top: 0.5rem;
  }

  .vs-vital-signs-table__cell-type {
    min-width: 100px;
  }

  .vs-vital-type {
    font-size: 0.75rem;
  }

  .vs-vital-type i {
    display: none;
  }
}

@media (max-width: 480px) {
  .vs-filters-grid {
    grid-template-columns: 1fr;
  }

  .vs-resident-card__avatar {
    width: 48px;
    height: 48px;
  }

  .vs-resident-card__name {
    font-size: 1rem;
  }
}
</style>